import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ChevronRight } from 'lucide-react';
import { Card, EmptyCircle, Skeleton } from '@/components/ui';
import { CoinsCircleIcon, DollarCircleIcon, PartialBadgeIcon, PendingBadgeIcon, PieCircleIcon } from './PortfolioCardIcons';
import { cn } from '@/lib/utils';
import { usePredictionMarketEventsFindById, useTradingOrderCancelOrder } from '@/lib/endpoints';
import type { PlaeeOrderResponseDto } from '@/lib/models';
import { invalidateTradingCaches } from '@/lib/trading/invalidate-trading-caches';
import { ORDER_FAILURE_MESSAGE, toOrderFailureReason } from '@/lib/trading/order-failure';
import { fmtDateTime, fmtUsd } from '@/lib/trading/format';
import { formatEventDate } from '@/lib/trading/portfolio-groups';
import { useCapabilityEnabled, KILL_SWITCH } from '@/lib/launchdarkly/killSwitches';
import type { BuyMarketContext } from '../BinaryMarkets/BuyInPopup';

export interface PortfolioOpenOrderCardProps {
  order: PlaeeOrderResponseDto;
  /** Open the modify (amend) sheet with the order's resolved market context. */
  onModify: (order: PlaeeOrderResponseDto, context: BuyMarketContext) => void;
  className?: string;
}

/**
 * Portfolio Open Orders card (Figma 1812:54263) — one resting order: category
 * line + Pending/Partial chip, question + close date, "Your Order" (answer +
 * Buy/Sell chip) with the placed time, Limit Price / Quantity / Total stats, a
 * fill-progress bar once partially filled, and **Modify** / **Cancel**.
 *
 * Cancel fires immediately (the already-filled portion stays a position, as on
 * the market page). Modify needs the order's option as market context — fetched
 * lazily from `/prediction-market/events/:eventId` on first click, then handed
 * up to the page-level {@link LimitOrderPopup}. Only buy orders are modifiable
 * (no sell-order entry sheet in scope), matching the market page.
 */
export function PortfolioOpenOrderCard({ order, onModify, className }: PortfolioOpenOrderCardProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const tradingEnabled = useCapabilityEnabled(KILL_SWITCH.TRADING_ORDER_PLACEMENT);
  const amendEnabled = useCapabilityEnabled(KILL_SWITCH.TRADING_AMEND);
  const [modifyRequested, setModifyRequested] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isBuy = order.action === 'buy';
  const isPartial = order.cumQty > 0;
  const fillPct = order.orderQty > 0 ? Math.round((order.cumQty / order.orderQty) * 100) : 0;
  const totalDollars = (order.price / 100) * order.orderQty;

  // Market context for the Modify sheet — fetched on demand, only when the
  // user asks (disabled query + imperative refetch; the cache dedupes repeats).
  const eventQuery = usePredictionMarketEventsFindById(order.eventId ?? '', { query: { enabled: false } });

  // Fire-and-forget from onClick: safe because the app's QueryClient doesn't
  // set `throwOnError`, so a failed refetch resolves (data undefined) instead
  // of rejecting — the error branch below covers it.
  async function handleModify() {
    if (!order.eventId || modifyRequested) return;
    setError(null);
    setModifyRequested(true);
    const { data: event } = await eventQuery.refetch();
    setModifyRequested(false);
    const option = (event?.markets ?? [])
      .flatMap((m) => m.options)
      .find((o) => o.yes.externalOutcomeId === order.outcomeId || o.no.externalOutcomeId === order.outcomeId);
    if (event && option) onModify(order, { ...option, eventName: event.name });
    else setError('This market is no longer available to modify.');
  }

  const cancelMutation = useTradingOrderCancelOrder({
    mutation: {
      onSuccess: (result) => {
        if (!result.success) {
          setError(ORDER_FAILURE_MESSAGE[toOrderFailureReason(result.errorCode)]);
          return;
        }
        invalidateTradingCaches(queryClient);
      },
      onError: () => setError(ORDER_FAILURE_MESSAGE.network),
      onSettled: () => setCancelling(false),
    },
  });

  function handleCancel() {
    if (!tradingEnabled || !order.clientOrderId || cancelling) return;
    setError(null);
    setCancelling(true);
    cancelMutation.mutate({
      orderId: order.orderId || order.clientOrderId,
      data: {
        origClientOrderId: order.clientOrderId,
        outcomeId: order.outcomeId,
        side: order.contractSide,
        action: order.action,
        count: order.leavesQty,
      },
    });
  }

  return (
    <Card
      variant="default"
      className={cn(
        'flex flex-col gap-3 rounded-[12px] border-stroke-soft p-3.5 shadow-[0_0_4px_0_var(--stroke-soft)]',
        className,
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <EmptyCircle
            src={order.eventImageUrl ?? order.categoryIconUrl}
            alt={order.eventTitle ?? ''}
            size="sm"
            className="size-9"
          />
          <span className="truncate text-base font-normal text-purple">{order.category ?? 'Prediction'}</span>
        </div>
        <span
          className={cn(
            'flex h-[30px] shrink-0 items-center justify-center gap-1 rounded-pill px-2.5',
            'text-base font-semibold capitalize tracking-[-0.32px]',
            isPartial ? 'bg-partial-soft text-partial' : 'bg-pending-soft text-pending',
          )}
        >
          {isPartial ? <PartialBadgeIcon /> : <PendingBadgeIcon />}
          {isPartial ? 'Partial' : 'Pending'}
        </span>
      </div>

      <div className="flex flex-col gap-1 px-4">
        <h3 className="text-lg font-semibold text-ink leading-snug tracking-[-0.36px]">
          {order.eventTitle ?? order.marketName ?? 'Market'}
        </h3>
        {order.closesAt && (
          <p className="text-base font-light text-ink tracking-[-0.32px]">{formatEventDate(order.closesAt)}</p>
        )}
        <div className="flex flex-wrap items-center gap-1 text-base font-semibold tracking-[-0.32px]">
          <span className="text-ink">Your Order:</span>
          <span className="truncate text-brand-grad-deep">
            {order.outcomeName ?? (order.contractSide === 'yes' ? 'Yes' : 'No')}
          </span>
          <span
            className={cn(
              'flex w-11 items-center justify-center rounded-[32px]',
              isBuy ? 'bg-up/10 text-up' : 'bg-down/10 text-down',
            )}
          >
            {isBuy ? 'Buy' : 'Sell'}
          </span>
        </div>
        <p className="text-sm text-ink-muted tracking-[-0.28px]">Placed {fmtDateTime(order.placedAt)}</p>
      </div>

      <div className="grid h-[50px] grid-cols-[1fr_1px_1fr_1px_1fr] items-center rounded-pill bg-surface-soft px-2">
        <OrderStat icon={<PieCircleIcon />} label="Limit Price" value={`${order.price}¢`} />
        <div className="h-[34px] w-px bg-border" aria-hidden />
        <OrderStat icon={<CoinsCircleIcon />} label="Quantity" value={String(order.orderQty)} />
        <div className="h-[34px] w-px bg-border" aria-hidden />
        <OrderStat icon={<DollarCircleIcon />} label="Total" value={fmtUsd(totalDollars)} />
      </div>

      {isPartial && (
        <div className="flex flex-col gap-1.5">
          <div className="flex items-center justify-between text-sm tracking-[-0.28px]">
            <span className="text-ink-muted">
              Filled {order.cumQty} of {order.orderQty}
            </span>
            <span className="text-purple tabular-nums">{fillPct}%</span>
          </div>
          <div className="h-2 overflow-hidden rounded-pill bg-border">
            <div
              className="h-full rounded-pill bg-gradient-to-r from-brand-grad-3 via-brand-grad-2 to-brand-grad-1"
              style={{ width: `${fillPct}%` }}
            />
          </div>
        </div>
      )}

      <div className="flex items-center gap-2">
        {isBuy && (
          <button
            type="button"
            onClick={handleModify}
            disabled={!tradingEnabled || !amendEnabled || !order.clientOrderId || !order.eventId || modifyRequested}
            className="h-10 flex-1 rounded-pill bg-purple text-lg font-semibold capitalize tracking-[-0.36px] text-white disabled:cursor-not-allowed disabled:opacity-50"
          >
            {modifyRequested ? 'Loading…' : 'Modify'}
          </button>
        )}
        <button
          type="button"
          onClick={handleCancel}
          disabled={!tradingEnabled || !order.clientOrderId || cancelling}
          className="h-10 flex-1 rounded-pill bg-chip-red text-lg font-semibold capitalize tracking-[-0.36px] text-down disabled:cursor-not-allowed disabled:opacity-50"
        >
          {cancelling ? 'Cancelling…' : 'Cancel'}
        </button>
      </div>
      {error && <p className="text-center text-[13px] font-medium text-danger-alt">{error}</p>}

      <div className="h-px w-full bg-border" aria-hidden />

      {order.eventId && (
        <button
          type="button"
          onClick={() => navigate(`/events/${order.eventId}`)}
          className="flex cursor-pointer items-center justify-center gap-1 self-end text-base font-normal text-purple underline tracking-[-0.32px] transition-colors hover:text-brand-grad-deep"
        >
          <span>Go to Market</span>
          <ChevronRight className="size-4" aria-hidden />
        </button>
      )}
    </Card>
  );
}

function OrderStat({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="flex min-w-0 items-center justify-center gap-1.5">
      {icon}
      <div className="flex min-w-0 flex-col">
        <span className="whitespace-nowrap text-sm font-normal text-ink tracking-[-0.28px]">{label}</span>
        <span className="truncate text-base font-semibold text-ink tracking-[-0.32px]">{value}</span>
      </div>
    </div>
  );
}

/**
 * Loading placeholder mirroring a {@link PortfolioOpenOrderCard}: category row
 * + status chip, title, date, order + placed lines, the three-cell stats box,
 * and the Modify/Cancel button row.
 */
export function PortfolioOpenOrderCardSkeleton() {
  return (
    <Card variant="default" className="flex flex-col gap-3 p-3.5">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Skeleton className="size-[30px] shrink-0 rounded-full" />
          <Skeleton className="h-4 w-36 rounded" />
        </div>
        <Skeleton className="h-6 w-16 rounded-pill" />
      </div>
      <div className="flex flex-col gap-1.5">
        <Skeleton className="h-5 w-3/4 rounded" />
        <Skeleton className="h-4 w-32 rounded" />
        <Skeleton className="mt-1 h-4 w-44 rounded" />
        <Skeleton className="h-3 w-24 rounded" />
      </div>
      <div className="flex items-stretch gap-2 rounded-card border border-border bg-surface-muted/60 px-3.5 py-2.5">
        {[0, 1, 2].map((i) => (
          <div key={i} className="flex flex-1 items-stretch gap-2">
            {i > 0 && <div className="w-px self-stretch bg-border" aria-hidden />}
            <div className="flex flex-1 flex-col gap-1.5">
              <Skeleton className="h-3 w-16 rounded" />
              <Skeleton className="h-5 w-12 rounded" />
            </div>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2">
        <Skeleton className="h-10 flex-1 rounded-pill" />
        <Skeleton className="h-10 flex-1 rounded-pill" />
      </div>
    </Card>
  );
}
